import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';
import { logger } from '../../helpers/logger.helper';
import {
  CHALLENGE_API_ERRORS,
  REMIND_DEFAULT_COOLDOWN_MINUTES,
} from '../constants/challenges.constants';
import { extractApiErrorMessage } from './challengeApiError.helper';

/** Authenticated API client signature (matches `useApiClient()`). */
type ApiClientFn = (endpoint: string, config?: AxiosRequestConfig) => Promise<AxiosResponse>;

/**
 * Cancel/delete a challenge.
 */
export const cancelChallenge = async (
  challengeId: string,
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const cancelEndpoint = `${API_ENDPOINTS.challenges.list}/${challengeId}`;
    const response = await apiClientFn(cancelEndpoint, { method: 'DELETE' });
    return { success: response.data?.success ?? false, error: response.data?.error };
  } catch (err: unknown) {
    return {
      success: false,
      error: extractApiErrorMessage(err, CHALLENGE_API_ERRORS.CANCEL_FAILED),
    };
  }
};

/**
 * Send a remind notification to pending participants (creator only).
 * Returns retryAfterMinutes when status is 429 (cooldown).
 */
export const remindChallenge = async (
  challengeId: string,
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; error?: string; retryAfterMinutes?: number }> => {
  try {
    const remindEndpoint = `${API_ENDPOINTS.challenges.list}/${challengeId}/remind`;
    const response = await apiClientFn(remindEndpoint, {
      method: 'POST',
      data: {},
      validateStatus: (status) => status === 200 || status === 429,
    });

    if (response.status === 429) {
      const data = response.data as { retryAfterMinutes?: number };
      return {
        success: false,
        error: CHALLENGE_API_ERRORS.REMIND_COOLDOWN,
        retryAfterMinutes: data?.retryAfterMinutes ?? REMIND_DEFAULT_COOLDOWN_MINUTES,
      };
    }

    return {
      success: response.data?.success ?? false,
      error: response.data?.error,
      retryAfterMinutes: undefined,
    };
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number; data?: { error?: string; retryAfterMinutes?: number } }; message?: string };
    if (ax?.response?.status === 429) {
      const data = ax.response?.data;
      return {
        success: false,
        error: CHALLENGE_API_ERRORS.REMIND_COOLDOWN,
        retryAfterMinutes: data?.retryAfterMinutes ?? REMIND_DEFAULT_COOLDOWN_MINUTES,
      };
    }
    const error = extractApiErrorMessage(err, CHALLENGE_API_ERRORS.REMIND_FAILED);
    logger.warn('[remindChallenge] Request failed', {
      status: ax?.response?.status,
      error,
      message: ax?.message,
    });
    return { success: false, error };
  }
};

/**
 * Decline a challenge invitation.
 */
export const declineChallenge = async (
  challengeId: string,
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const declineEndpoint = `${API_ENDPOINTS.challenges.list}/${challengeId}/decline`;
    const response = await apiClientFn(declineEndpoint, { method: 'POST', data: {} });
    return { success: response.data?.success ?? false, error: response.data?.error };
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number }; message?: string };
    const error = extractApiErrorMessage(err, CHALLENGE_API_ERRORS.DECLINE_FAILED);
    logger.warn('[declineChallenge] Request failed', {
      status: ax.response?.status,
      error,
      message: ax.message,
    });
    return { success: false, error };
  }
};

/**
 * Accept a challenge invitation.
 */
export const acceptChallenge = async (
  challengeId: string,
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const acceptEndpoint = `${API_ENDPOINTS.challenges.list}/${challengeId}/accept`;
    const response = await apiClientFn(acceptEndpoint, { method: 'POST', data: {} });
    return { success: response.data?.success ?? false, error: response.data?.error };
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number }; message?: string };
    const error = extractApiErrorMessage(err, CHALLENGE_API_ERRORS.ACCEPT_FAILED);
    logger.warn('[acceptChallenge] Request failed', {
      status: ax.response?.status,
      error,
      message: ax.message,
    });
    return { success: false, error };
  }
};

/** Payload for a single pick in submit-picks. */
export interface SubmitPickPayload {
  matchId: string;
  marketId?: number;
  marketName?: string;
  value: string;
  odd?: number;
}

/** What submit-picks reports back about the accept side of the request. */
export interface SubmitPicksResult {
  /** An existing invitation flipped PENDING → ACCEPTED as part of this request. */
  wasPending: boolean;
  challengeActivated: boolean;
  /** This request claimed an open-board challenge's opponent slot (specs/open-challenges AC-8). */
  tookOnOpen?: boolean;
}

/**
 * Submit all picks for a challenge and accept if participant was PENDING (atomic).
 *
 * Also the take-on path for an open-board challenge: the caller is not a participant yet, and the
 * server claims the slot and stakes inside the same transaction that writes these picks. Prefer
 * sending marketName for non-1X2 markets so display names are correct.
 */
export const submitChallengePicks = async (
  challengeId: string,
  picks: SubmitPickPayload[],
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; data?: SubmitPicksResult; error?: string }> => {
  try {
    const submitEndpoint = `${API_ENDPOINTS.challenges.list}/${challengeId}/submit-picks`;
    const response = await apiClientFn(submitEndpoint, {
      method: 'POST',
      data: { picks },
    });
    return {
      success: response.data?.success ?? false,
      data: response.data?.data,
      error: response.data?.error,
    };
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number }; message?: string };
    const error = extractApiErrorMessage(err, CHALLENGE_API_ERRORS.SUBMIT_PICKS_FAILED);
    logger.warn('[submitChallengePicks] Request failed', {
      status: ax?.response?.status,
      error,
      message: ax?.message,
    });
    return { success: false, error };
  }
};

/**
 * Update predictions in batch.
 */
export const updatePredictionsBatch = async (
  predictions: Array<{
    challengeId: string;
    matchId: string;
    userId: string;
    prediction: 'home' | 'draw' | 'away';
    homeScore: number;
    awayScore: number;
    outcome: 'home' | 'draw' | 'away';
  }>,
  apiClientFn: ApiClientFn,
): Promise<{ success: boolean; error?: string }> => {
  try {
    await apiClientFn(API_ENDPOINTS.predictions.batch, {
      method: 'PUT',
      data: { predictions },
    });
    return { success: true };
  } catch (err: unknown) {
    return {
      success: false,
      error: extractApiErrorMessage(err, CHALLENGE_API_ERRORS.UPDATE_PREDICTIONS_FAILED),
    };
  }
};
